import { useCallback, useEffect, useState } from "react";
import { showNotification } from "../../utils/notification";
import { t } from "i18next";
import Loader from "../global/Loader";
import { Button, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faRefresh } from "@fortawesome/free-solid-svg-icons";
import { Trans } from "react-i18next";
import useClientProfessionApi from "../../app/useClientProfessionApi";
import ProfessionTable from "./ProfessionTable";
import ProfessionEdit from "./ProfessionEdit";
import useDebounce from "../../utils/useDebounce";
import FilterStringInput from "../global/FilterStringInput";

function ProfessionTab() {
    const clientRiskApi = useClientProfessionApi();

    const [professions, setProfessions] = useState(null);
    const [reloadRequest, setReloadRequest] = useState(false);

    const [filterStr, setFilterStr, debouncedFilterStr] = useDebounce("", 500);
    const [loading, setLoading] = useState(false);

    const [showClientOnly, setShowClientOnly] = useState(true);
    const [addFormOpen, setAddFormOpen] = useState(false);

    const onSave = useCallback(async (profession) => {
        if (profession.id) {
            try {
                const updatedProfession = await clientRiskApi.updateProfession(profession);

                const newProfessions = professions.map((p) => {
                    if (p.id === profession.id) {
                        return updatedProfession;
                    }
                    return p;
                });
                setProfessions(newProfessions);
                showNotification(t("professionTableUpdateSuccess"), 'success');
            } catch (error) {
                console.error(error);
                showNotification(t("professionTableUpdateError") + ": " + error.message, 'danger');
            }
        } else {
            try {
                const p = await clientRiskApi.addProfession(profession);

                setProfessions([...professions, p]);
                showNotification(t("professionTableCreateSuccess"), 'success');
            } catch (error) {
                console.error(error);
                showNotification(t("professionTableCreateError") + ": " + error.message, 'danger');
            }
        }
    }, [clientRiskApi, professions]);

    const onDelete = useCallback(async (id) => {
        try {
            await clientRiskApi.deleteProfession({ id });

            const newProfessions = professions.filter((p) => p.id !== id);
            setProfessions(newProfessions);
            showNotification(t("professionTableDeleteSuccess"), 'success');
        } catch (error) {
            console.error(error);
            showNotification(t("professionTableDeleteError") + ": " + error.message, 'danger');
        }
    }, [clientRiskApi, professions]);

    useEffect(() => {
        async function fetchData() {
            setLoading(true);

            try {
                const data = await clientRiskApi.getProfessions(showClientOnly, debouncedFilterStr);
                setProfessions(data);
            } catch (error) {
                console.error(error);
                showNotification(t("riskFactorTableFetchError") + ": " + error.message, 'danger');
            }

            setLoading(false);
        }

        const delayedFetch = setTimeout(fetchData, 200);
        return () => clearTimeout(delayedFetch);
    }, [clientRiskApi, debouncedFilterStr, reloadRequest, showClientOnly]);

    return (
        professions === null ? (
            <Loader />
        ) : (
            <div className="mt-4">
                {/* Buttons toolbar */}
                <div className="mt-3 d-flex justify-content-between align-items-center gap-2">
                    <Button
                        variant="primary"
                        onClick={() => setReloadRequest((prev) => !prev)}>
                        <FontAwesomeIcon icon={faRefresh} /> <Trans>professionTableReload</Trans>
                    </Button>

                    <div className="flex-grow-1">
                        <FilterStringInput filterStr={filterStr} setFilterStr={setFilterStr} />
                    </div>

                    <Form.Check
                        type="switch"
                        id="showClientOnly"
                        label={t("professionTableShowClientOnly")}
                        checked={showClientOnly}
                        onChange={(e) => setShowClientOnly(e.target.checked)}
                    />
                </div>

                {/* Main table */}
                {!loading ? (
                    <ProfessionTable professions={professions} showClientOnly={showClientOnly} onSave={onSave} onDelete={onDelete} />
                ) : (
                    <Loader />
                )}

                {/* Add form */}
                <div className="d-flex justify-content-center mt-3">
                    <Button
                        variant="primary"
                        onClick={() => setAddFormOpen(true)}>
                        <FontAwesomeIcon icon={faPlus} /> <Trans>professionTableAdd</Trans>
                    </Button>
                </div>

                {addFormOpen && (
                    <ProfessionEdit
                        profession={{}}
                        onSave={onSave}
                        onCancel={() => setAddFormOpen(false)}
                    />
                )}
            </div>
        )
    );
}

export default ProfessionTab;
